import { color, Common, device } from '@stockbit/ui';
import styled, { css, keyframes } from 'styled-components';

const { Flex, Text, Modal } = Common;

export const ShopContainer = styled.div``;

export const HeaderWrapper = styled(Flex).attrs({
  justify: 'space-between',
  align: 'center',
  px: '20px',
})`
  border: 1px solid ${color.gainsboro};
  background: ${color.light};
  border-radius: 4px;
  height: 50px;
  margin-bottom: 24px;
`;

export const ShopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${color.light};
  border-top: 0;
  border-radius: 2px;
  gap: 24px;

  @media ${device.mobileS} {
    margin: 20px;
  }

  @media ${device.mobileL} {
    margin: 40px 60px;
  }
`;

export const ShopBody = styled.div`
  position: relative;

  * > img:first-child {
    border: 1px solid red;
  }

  span.highlight {
    position: relative;
    z-index: 1;
    white-space: nowrap;

    &:after {
      content: '';
      position: absolute;
      z-index: -1;
      right: -4px;
      display: block;
      background-color: #cceee1;
      opacity: 0.8;
      width: 225px;
      height: 10px;
      bottom: 1px;
    }
  }

  span.price {
    color: ${color.primary};
    white-space: nowrap;
  }

  @media ${device.mobileS} {
    padding: 0;
    height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media ${device.mobileL} {
    padding: 32px;
    display: block;
    height: 100%;
  }
`;

export const ShopTextWrapper = styled.div`
  width: 100%;
`;

export const ShopFooter = styled.div`
  width: 100%;
  max-width: 648px;
  margin: auto;

  button {
    width: 100%;
    border-radius: 4px;
  }
`;

export const StarContainer = styled.div<{ position?: 'top' | 'bottom' }>`
  position: absolute;
  ${({ position }) => {
    if (position === 'top') {
      return css`
        top: 0;

        @media ${device.mobileS} {
          left: 0;
        }

        @media ${device.mobileL} {
          left: -15px;
        }
      `;
    }

    return css`
      bottom: 0;

      @media ${device.mobileS} {
        right: 0;
      }

      @media ${device.mobileL} {
        right: -15px;
      }

      * img {
        rotate: 180deg;
      }
    `;
  }}
`;

export const TabWrapper = styled.div`
  background-color: ${color.light};
  border: 1px solid ${color.gainsboro};
  border-radius: 4px;
`;

export const HistoryContainer = styled.div``;

export const HistoryPanel = styled.div`
  padding: 0 20px;
  &:not(:last-child) {
    border-bottom: 8px solid #f7f7f7;
  }
`;

export const HistoryPanelHeader = styled.div`
  padding: 24px 0px;
`;

export const HistoryPanelList = styled.div`
  padding: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 100%;
  background-color: #ebf8f3;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  border: 1px solid ${color.downgray};
`;

const CardHeader = styled.div`
  width: 100%;
  padding: 16px 20px;
  background-color: ${color.light};
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f1f1f1;
`;

const CardContent = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 431px minmax(0, 1fr);

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const CardColumn = styled.div`
  padding: 20px;
  background-color: ${color.light};
  display: flex;
  flex-direction: column;

  p.title {
    margin-bottom: 20px;
  }

  button.btn-cart-submit {
    border-radius: 4px;
    margin-top: 2px;
  }

  @media ${device.tablet} {
    padding: 40px;
  }
`;

const LoadingPaymentModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 3px;
    max-width: 320px;
    margin: auto;
`;

const FailedPayment = styled(Text).attrs({
  as: 'div',
  align: 'center',
})`
  border-radius: 2px;
  color: #fff;
  background: ${color.sunsetOrange};
  padding: 5px;
`;

const PaymentMethod = styled.div`
  height: 63px;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #f1f1f1;
  margin-bottom: 12px;
  border-radius: 4px;

  * {
    cursor: pointer;
  }

  input[type='radio'] {
    display: none;
  }

  label {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  input:checked + label {
    .checkmark {
      background-color: ${color.primary};
      border: 0;
    }
  }
`;

const CheckmarkWrapper = styled.div`
  width: 20px;
  height: 20px;
`;

const Checkmark = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 100%;
  border: 1px solid ${color.gainsboro};
`;

const Divider = styled.div`
  width: 100%;
  height: 10px;
  border-bottom: 1px solid #f1f1f1;
  margin-bottom: 16px;
`;

const VoucherWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${color.gainsboro};
  padding: 12px;
  margin-top: 12px;
  margin-bottom: 18px;
`;

interface ShimmerProps {
  width?: string;
  height?: string;
}

const keyframesShimmer = keyframes`
  0% {
    background-position: -80vw 0;
  }
  100% {
    background-position: 80vw 0;
  }
`;

const shimmerAnimation = css`
  background: linear-gradient(to right, #f7f7f7, #f7f7f7, #f7f7f7);
  background-size: 80vw 100%;
  animation: ${keyframesShimmer} 2s infinite linear;
`;

const Shimmer = styled.div<ShimmerProps>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '24px'};
  background-color: #f7f7f7;
  border-radius: 4px;
  ${shimmerAnimation}
`;

const ShimmerFlex = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const ShimmerHeaderWrapper = styled(ShimmerFlex)`
  padding: 40px 90px;
  width: 100%;
  max-width: 648px;
  margin: auto;
`;

export const ShimmerBox = styled(ShimmerFlex)`
  justify-content: center;
  padding: 12px 24px;
  border-radius: 4px;
  border: 1px solid ${color.downgray};
  min-height: 100px;
`;

export const ShimmerButton = styled(ShimmerFlex)`
  width: 100%;
  max-width: 648px;
  margin: auto;
`;

const WaitingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 40px 60px;
  height: 500px;
`;

const WaitingTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 350px;
`;

const WaitingTextTitle = styled(Text).attrs({
  size: '18px',
})`
  text-align: center;
  font-weight: 700;
  line-height: 135%;
`;

const WaitingTextDescription = styled(Text).attrs({
  size: '16px',
})`
  text-align: center;
  font-weight: 700;
  color: ${color.highgray};
`;

export {
  CardHeader,
  CardContent,
  CardColumn,
  PaymentMethod,
  Checkmark,
  CheckmarkWrapper,
  Divider,
  VoucherWrapper,
  LoadingPaymentModal,
  FailedPayment,
  Shimmer,
  WaitingWrapper,
  WaitingTextWrapper,
  WaitingTextTitle,
  WaitingTextDescription,
};
